import { Anchor } from '@mantine/core';
import {
  QuestionnaireItem,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { QuestionnaireFormItem } from './QuestionnaireFormItem';
import { FormSection, QuestionnaireItemType } from '@medplum/react';
import { useRepeatableItemCount } from '../hooks/useQuestionnaireItemState';
import { RENDERING_STYLE_EXTENSION_URL } from '@consts/systems.consts';


interface QuestionnaireRepeatableItemProps {
  readonly item: QuestionnaireItem;
  readonly response?: QuestionnaireResponseItem;
  readonly checkForQuestionEnabled: (
    item: QuestionnaireItem,
    contextResponse?: any
  ) => boolean;
  readonly onChange: (items: QuestionnaireResponseItem[]) => void;
}

export const QuestionnaireRepeatableItem = (
  props: QuestionnaireRepeatableItemProps
): JSX.Element | null => {
  const { item, response, onChange } = props;
  const number = useRepeatableItemCount(
    item,
    response ?? { linkId: item.linkId }
  );

  //----------------------------------------------------------------------------
  /**
   * extract styling for questionnaire's item
   * mainly to control the size
   */
  const cssClass =
    item.extension?.find((ext) => ext.url === RENDERING_STYLE_EXTENSION_URL)
      ?.valueString || 'flex-auto';

  //----------------------------------------------------------------------------
  if (!props.checkForQuestionEnabled(item)) {
    return null;
  }

  if (!response) {
    return null;
  }

  if (item.type === QuestionnaireItemType.display) {
    return (
      <p className={cssClass} key={item.linkId}>
        {item.text}
      </p>
    );
  }

  const showAddButton =
    item?.repeats &&
    item.type !== QuestionnaireItemType.choice &&
    item.type !== QuestionnaireItemType.openChoice;

  // Styling reason to avoid duplicate text
  if (item.type === QuestionnaireItemType.boolean) {
    return (
      <QuestionnaireFormItem
        key={item.linkId}
        item={item}
        response={response}
        onChange={(r) => onChange([r])}
        index={0}
      />
    );
  }

  return (
    <div className={cssClass}>
      <FormSection
        key={props.item.linkId}
        htmlFor={props.item.linkId}
        title={props.item.text}
        withAsterisk={props.item.required}
      >
        {[...Array(number)].map((_, index) => (
          <QuestionnaireFormItem
            key={`${item.linkId}-${index}`}
            item={item}
            response={response}
            onChange={(r) => onChange([r])}
            index={index}
          />
        ))}
        {showAddButton && (
          <Anchor
            onClick={() => {
              const newAnswer = { valueString: '' };
              const updatedResponse = {
                ...response,
                answer: [...(response.answer || []), newAnswer],
              };
              onChange([updatedResponse]);
            }}
          >
            Add Item
          </Anchor>
        )}
      </FormSection>
    </div>
  );
};
